/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { normalizeThreadSandboxMode, resolveSelectableComposerModelId } from '@/lib/pieveComposerSettings.js';
import { DEFAULT_PIEVE_MODEL_ID } from '@shared/pieve/defaultModel.js';
import buildPieveWorkflowWebhookUrl from '@/lib/workflows/buildPieveWorkflowWebhookUrl.js';
import { resolveWorkflowPlanKnowledgePath } from '@shared/pieve/workflowPlanKnowledgePath.js';
import normalizeThreadDebugMode from '@/lib/normalizeThreadDebugMode.js';
import { COMPOSER_MODEL_OPTIONS, EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { knowledgeItems } from '@/redux/selectors/pieveSelectors/sidebarSkillSelectors.js';
import { companyEnvironments, rawWorkflowRows, threadAppConnectionStatus, threadAppConnectionStatusThreadId, threadMetaById, workflowActions, workflowActionsLatestRunStartedOn, workflowActionsThreadId, workflowRunList, workflowStepPendingEdits, workflowWebhookSamples, workflowWebhookSamplesWorkflowId } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import applyWorkflowStepPendingEdits from '@/redux/selectors/pieveSelectors/lib/applyWorkflowStepPendingEdits.js';
import hydrateWorkflowStepApps from '@/redux/selectors/pieveSelectors/lib/hydrateWorkflowStepApps.js';
import buildAppMetadataMaps from '@/redux/selectors/pieveSelectors/lib/buildAppMetadataMaps.js';
import buildConnectionRailRows from '@/redux/selectors/pieveSelectors/lib/buildConnectionRailRows.js';
import buildStepEnvironmentConnectionRefs from '@/redux/selectors/pieveSelectors/lib/buildStepEnvironmentConnectionRefs.js';
import resolveWorkflowRunEnvironments from '@/redux/selectors/pieveSelectors/lib/resolveWorkflowRunEnvironments.js';
import pickWorkflowRunEnvironment from '@/redux/selectors/pieveSelectors/lib/pickWorkflowRunEnvironment.js';
import buildWorkflowActionDetails from '@/redux/selectors/pieveSelectors/lib/buildWorkflowActionDetails.js';
import buildThreadInboxSummary from '@/redux/selectors/pieveSelectors/lib/buildThreadInboxSummary.js';
import sortWorkflowRunsByStartedAt from '@/redux/selectors/pieveSelectors/lib/sortWorkflowRunsByStartedAt.js';
import resolveActiveWorkflowRunStepId from '@/redux/selectors/pieveSelectors/lib/resolveActiveWorkflowRunStepId.js';
import buildComposerModelState from '@/redux/selectors/pieveSelectors/lib/buildComposerModelState.js';
import buildComposerRunTarget from '@/redux/selectors/pieveSelectors/lib/buildComposerRunTarget.js';
import getComposerSelectorSurfaceMode from '@/redux/selectors/pieveSelectors/lib/getComposerSelectorSurfaceMode.js';
import getComposerSelectorThreadId from '@/redux/selectors/pieveSelectors/lib/getComposerSelectorThreadId.js';
import normalizeResolvedComposerSurfaceMode from '@/redux/selectors/pieveSelectors/lib/normalizeResolvedComposerSurfaceMode.js';
import { activeThreadId } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';
import { isActiveThreadOwnedByViewer, threadType } from '@/redux/selectors/pieveSelectors/threadCoreSelectors.js';
import { pref as selectPref, workflowRunEnvironmentIdById } from '@/redux/selectors/uiSelectors.js';
import { companyId, userId } from '@/redux/selectors/userSelectors.js';

export const composerModelOptions = () => COMPOSER_MODEL_OPTIONS;
export const defaultComposerModelId = () => DEFAULT_PIEVE_MODEL_ID;

export const isActiveThreadOnboarding = (state) => {
  const threadId = activeThreadId(state);
  return Boolean(threadId) && threadType(state, threadId) === 'onboarding';
};

// `activeThreadSurfaceMode` folds workflow_requirements into the shared 'workflow' surface, so
// surfaces that must distinguish the planning phase from the build phase read the raw type here.
export const isActiveThreadWorkflowRequirements = (state) => {
  const threadId = activeThreadId(state);
  return Boolean(threadId) && threadType(state, threadId) === 'workflow_requirements';
};

// The stored draft with the person's step edits still in flight laid over it, so every reader sees what they typed.
export const rawActiveThreadDraftWorkflow = createSelector([rawWorkflowRows, activeThreadId, workflowStepPendingEdits], (workflows, threadId, pendingEdits) => {
  const workflow = workflows.find((w) => w?.threadId === threadId && w?.version === 'draft') || null;

  return applyWorkflowStepPendingEdits({ workflow, pendingEdits });
});

export const rawAppsMetadataItems = (state) => state?.pieve?.appsMetadataItems || [];

// App icons live in client app metadata, not in the per-app status responses, so anything drawing an
// app resolves them here — the same source and the same lookups the workflow steps are enriched from,
// keyed by id and by slug because callers carry one or the other.
export const appMetadataMaps = createSelector([rawAppsMetadataItems], (appsMetadata) => {
  return buildAppMetadataMaps({
    appsMetadataItems: appsMetadata
  });
});

export const activeThreadDraftWorkflow = createSelector([rawActiveThreadDraftWorkflow, rawAppsMetadataItems], (workflow, appsMetadataItems) => {
  if (!Array.isArray(workflow?.definition?.steps)) {
    return workflow;
  }

  return {
    ...workflow,
    definition: { ...workflow.definition, steps: hydrateWorkflowStepApps({ steps: workflow.definition.steps, appsMetadataItems, appMetadataById: workflow.appMetadataById }) }
  };
});

export const activeThreadWorkflowId = (state) => {
  return activeThreadDraftWorkflow(state)?.workflowId || null;
};

// The draft's compare-and-swap counter: it rises on every write to the definition, and on nothing else.
export const activeThreadWorkflowRevision = (state) => {
  return rawActiveThreadDraftWorkflow(state)?.revision ?? null;
};

export const activeThreadWorkflowPlanPath = (state) => {
  return resolveWorkflowPlanKnowledgePath({
    workflowId: activeThreadWorkflowId(state),
    threadId: activeThreadId(state)
  });
};

export const activeThreadWorkflowRequirements = (state) => {
  const requirementsPath = activeThreadWorkflowPlanPath(state);

  if (!requirementsPath) {
    return null;
  }

  const knowledge = Array.isArray(state?.pieve?.knowledge) ? state.pieve.knowledge : [];
  return knowledge.find((item) => item?.path === requirementsPath) || null;
};

/* Scoped to the thread that owns the response, so a stale payload from a thread the user has navigated
   away from can never render — the same rule as the connection status above. One read covers both
   phases: the server answers from the thread's staged list before a draft exists and from the draft's
   meta after, so there is nothing here to pick between, and no second home to keep in step.
   Raw; every surface reads the labelled selector below. */
export const activeThreadWorkflowActions = createSelector([workflowActions, workflowActionsThreadId, activeThreadId], (actions, ownerThreadId, threadId) => {
  if (!threadId || ownerThreadId !== threadId) {
    return EMPTY_ARRAY;
  }

  return actions;
});

// The stored actions plus the labels the JSX cannot derive, open first; rail, modal and card all read this.
export const activeThreadWorkflowActionDetails = createSelector([activeThreadWorkflowActions, userId], (actions, currentUserId) => {
  return buildWorkflowActionDetails({ actions, currentUserId });
});

// Whether the active thread's list has arrived, so a surface can tell "not on the list" from "not loaded".
export const isActiveThreadWorkflowActionsLoaded = (state) => {
  const threadId = activeThreadId(state);

  return Boolean(threadId) && workflowActionsThreadId(state) === threadId;
};

// One of them, for the modal row that owns it.
export const activeThreadWorkflowActionDetail = (state, actionKey) => {
  return activeThreadWorkflowActionDetails(state).find((action) => action.key === actionKey) || null;
};

// What reached the thread since Pieve last ran (C5 answers plus apps connected elsewhere), for the thread
// header's Continue line. Scoped like the list it reads, so it can never describe another thread.
export const activeThreadInboxSummary = createSelector([activeThreadWorkflowActions, workflowActionsLatestRunStartedOn, userId], (actions, latestRunStartedOn, currentUserId) => {
  return buildThreadInboxSummary({ actions, latestRunStartedOn, currentUserId });
});

// One canonical answer to "does this thread render the workflow surface?". Read by the surface
// mount decision, by the sidebar sheet breakpoint (workflow threads go to a sheet a whole
// breakpoint earlier), and by the sidebar-dismiss helper. Covers the whole workflow family: build
// threads, planning threads before a draft exists, and any thread already owning a draft.
export const isActiveThreadWorkflowSurface = (state) => {
  return activeThreadSurfaceMode(state) === 'workflow' || Boolean(activeThreadWorkflowId(state));
};

// Scoped to the thread that owns the response, so a stale payload from a thread the user has navigated
// away from can never render. One read covers both phases — the server answers from the plan's declared
// apps before a draft exists and from the draft's steps after — so there is nothing here to pick between.
export const activeThreadAppConnectionStatus = createSelector([threadAppConnectionStatus, threadAppConnectionStatusThreadId, activeThreadId], (items, ownerThreadId, threadId) => {
  if (!threadId || ownerThreadId !== threadId) {
    return EMPTY_ARRAY;
  }

  return items;
});

// The workflow rail's Connections rows: the per-app status the server resolved, joined to client app
// metadata for icons and to the viewer's ownership of the thread for whether a Connect button belongs
// on the row. The section renders these rows and derives nothing of its own.
export const activeThreadStepEnvironmentConnectionRefs = createSelector([activeThreadDraftWorkflow, companyEnvironments], (draft, environments) => {
  return buildStepEnvironmentConnectionRefs({ definition: draft?.definition, environments });
});

// Where the active workflow's Test and Run now happen: every environment it can run in, and the one they use.
export const activeThreadWorkflowRunEnvironments = createSelector([activeThreadDraftWorkflow, companyEnvironments], (draft, environments) => {
  return resolveWorkflowRunEnvironments({ steps: draft?.definition?.steps, environments });
});

export const activeThreadWorkflowRunEnvironment = createSelector([activeThreadWorkflowRunEnvironments, activeThreadDraftWorkflow, workflowRunEnvironmentIdById, companyEnvironments], (runEnvironments, draft, pickedById, environments) => {
  return pickWorkflowRunEnvironment({ runEnvironments, pickedEnvironmentId: pickedById?.[draft?.workflowId] || null, environments });
});

export const activeThreadConnectionRailRows = createSelector([activeThreadAppConnectionStatus, appMetadataMaps, isActiveThreadOwnedByViewer, activeThreadStepEnvironmentConnectionRefs, companyEnvironments], (items, { metadataById, metadataBySlug }, isThreadOwner, environmentRefsByAppKey, environments) => {
  return buildConnectionRailRows({ items, metadataById, metadataBySlug, isThreadOwner, environmentRefsByAppKey, environments });
});

// Planning has no step to have picked a credential — and so no environment refs either — so it gets one
// row per app naming the connection the run would bind. Its own selector rather than an argument to the
// one above, because a `createSelector` takes no arguments without giving up its memoisation.
export const activeThreadPlannedConnectionRailRows = createSelector([activeThreadAppConnectionStatus, appMetadataMaps, isActiveThreadOwnedByViewer, companyEnvironments], (items, { metadataById, metadataBySlug }, isThreadOwner, environments) => {
  return buildConnectionRailRows({ items, metadataById, metadataBySlug, isThreadOwner, oneRowPerApp: true, environments });
});

// Everything the webhook rail card renders, in the shape it renders it. `url` is absent until the
// binding exists, which is what tells the card there is no webhook source step to describe.
export const activeThreadWorkflowWebhook = createSelector([activeThreadDraftWorkflow, companyId], (workflow, currentCompanyId) => {
  return {
    workflowId: workflow?.workflowId || null,
    threadId: workflow?.threadId || null,
    url: buildPieveWorkflowWebhookUrl({ companyId: currentCompanyId, pathSlug: workflow?.webhookPath }),
    isSecretRequired: workflow?.webhookSecretRequired === true,
    // The column defaults to 'async', so anything that is not explicitly 'sync' means do not wait.
    isWaitingForRun: workflow?.webhookResponseMode === 'sync'
  };
});

// Past deliveries for the workflow currently on screen. Scoped by workflow id because the collection
// outlives a route change, and showing another workflow's deliveries would be worse than showing none.
export const activeThreadWorkflowWebhookSamples = createSelector([workflowWebhookSamples, workflowWebhookSamplesWorkflowId, activeThreadWorkflowId], (samples, samplesWorkflowId, workflowId) => {
  if (!workflowId || samplesWorkflowId !== workflowId) {
    return EMPTY_ARRAY;
  }

  return samples;
});

export const activeThreadWorkflowRunsList = createSelector([activeThreadId, workflowRunList], (threadId, runs) => {
  if (!threadId) {
    return EMPTY_ARRAY;
  }

  return sortWorkflowRunsByStartedAt(runs.filter((run) => String(run?.threadId || '').trim() === String(threadId).trim()));
});

// The newest person's test on this workflow (a manual run carrying meta.test), for the Test window's way back to it.
export const activeThreadLatestWorkflowTestRun = createSelector([activeThreadWorkflowRunsList], (runs) => runs.find((run) => run?.meta?.test) || null);

/* The step the in-flight run is on, for the editor canvas. Null whenever nothing is running. */
export const activeThreadWorkflowRunStepId = createSelector([activeThreadWorkflowRunsList], resolveActiveWorkflowRunStepId);

const CHECKLIST_LINE_PATTERN = /^(\s*)- \[([ xX])\] (.+)$/;

export const onboardingChecklist = createSelector([knowledgeItems], (knowledge) => {
  const entry = knowledge.find((item) => item?.path === 'job/onboarding-checklist.md');
  const content = String(entry?.content || '').trim();

  if (!content) {
    return [];
  }

  const lines = content.split('\n');
  const root = [];
  const stack = [];

  for (const line of lines) {
    const match = line.match(CHECKLIST_LINE_PATTERN);

    if (!match) {
      continue;
    }

    const depth = Math.floor(match[1].length / 2);
    const checked = match[2].toLowerCase() === 'x';
    const label = match[3].trim();
    const item = { label, checked, children: [] };

    while (stack.length > depth) {
      stack.pop();
    }

    const parent = stack.length > 0 ? stack[stack.length - 1] : null;

    if (parent) {
      parent.children.push(item);
    } else {
      root.push(item);
    }

    stack.push(item);
  }

  return root;
});

export const activeThreadSurfaceMode = (state) => {
  const threadId = activeThreadId(state);
  if (!threadId) {
    return '';
  }
  return normalizeResolvedComposerSurfaceMode(threadType(state, threadId)) || '';
};

export const resolvedComposerSurfaceMode = (state, options = {}) => {
  const explicitSurfaceMode = getComposerSelectorSurfaceMode(options);

  if (explicitSurfaceMode) {
    return explicitSurfaceMode;
  }

  const normalizedThreadId = getComposerSelectorThreadId(options);

  if (!normalizedThreadId) {
    return '';
  }

  return normalizeResolvedComposerSurfaceMode(threadType(state, normalizedThreadId));
};

export const preferredComposerModelState = createSelector(
  [
    (state, options = {}) => {
      const normalizedThreadId = getComposerSelectorThreadId(options);

      if (normalizedThreadId) {
        return threadMetaById(state)?.[normalizedThreadId]?.composerModel || DEFAULT_PIEVE_MODEL_ID;
      }

      const prefSettings = selectPref(state, 'pieve.thread.defaults.composerSettings');
      return resolveSelectableComposerModelId(prefSettings?.composerModel || null);
    },
    (state, options = {}) => {
      const normalizedThreadId = getComposerSelectorThreadId(options);

      if (normalizedThreadId) {
        return threadMetaById(state)?.[normalizedThreadId]?.composerReasoningEffort;
      }

      return selectPref(state, 'pieve.thread.defaults.composerSettings')?.composerReasoningEffort;
    }
  ],
  (chosenModelId, rawReasoningEffort) => {
    return buildComposerModelState({
      modelId: chosenModelId,
      rawReasoningEffort
    });
  }
);

/* The run target of a composer: Pieve's models on the server, or the person's Claude plan through Pieve Desktop on
   this Mac (`kind`), with the model, provider, thinking level and limits the send and the picker read from one place.
   The Pieve preference the thread row holds stays readable through `preferredComposerModelState`. */
export const resolvedComposerModelState = createSelector([preferredComposerModelState], (preferredModelState) => {
  return buildComposerRunTarget({ pieveModelState: preferredModelState });
});

// The Pieve run target of a composer, whatever the plan pick: for a send whose runtime only Pieve's runs carry, such as the table editor's draft.
export const resolvedPieveComposerModelState = createSelector([preferredComposerModelState], (preferredModelState) => {
  return buildComposerRunTarget({ pieveModelState: preferredModelState });
});

export const resolvedComposerSandboxMode = (state, options = {}) => {
  const normalizedThreadId = getComposerSelectorThreadId(options);

  if (!normalizedThreadId) {
    return normalizeThreadSandboxMode(selectPref(state, 'pieve.thread.defaults.sandboxMode'));
  }

  return normalizeThreadSandboxMode(threadMetaById(state)?.[normalizedThreadId]?.sandboxMode);
};

export const resolvedComposerDebugMode = (state, options = {}) => {
  const normalizedThreadId = getComposerSelectorThreadId(options);

  if (!normalizedThreadId) {
    return normalizeThreadDebugMode(selectPref(state, 'pieve.thread.defaults.debugMode'));
  }

  return normalizeThreadDebugMode(threadMetaById(state)?.[normalizedThreadId]?.debugMode);
};
